import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_PROJECT_STATUSES } from '@lims/shared-types';
import { projectsApi } from '../../api/endpoints.js';
import type { ProjectSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordLink, ViewTabs } from '../clients/record-layout.js';

/**
 * The laboratory's projects.
 *
 * Cancelled and completed projects stay listed, marked. Closing a project
 * stops NEW work under it and removes nothing: every sample, result and report
 * ever produced under it is still recorded there.
 */
export function ProjectsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const projects = useQuery({
    queryKey: ['projects', { page, search, status }],
    queryFn: () =>
      projectsApi.list({
        page,
        search,
        ...(status ? { status } : {}),
      }),
  });

  const columns: readonly DataColumn<ProjectSummary>[] = [
    {
      key: 'name',
      header: t('projects.title'),
      render: (project) => (
        // The name opens the file: the row's way in (D278).
        <span className="font-semibold">
          <RecordLink to={`/projects/${project.id}`}>
            {project.name ?? t('common.notRecorded')}
          </RecordLink>
        </span>
      ),
    },
    {
      key: 'client',
      header: t('projects.client'),
      render: (project) => (
        <div className="min-w-0">
          <span className="block text-default-800" dir="auto">
            {project.clientName ?? project.clientNumber}
          </span>
          <span className="measure text-caption text-default-600" dir="ltr">
            {project.clientNumber}
          </span>
        </div>
      ),
    },
    {
      key: 'reference',
      header: t('projects.reference'),
      render: (project) =>
        project.reference ? (
          <span className="measure text-default-700" dir="ltr">
            {project.reference}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (project) => (
        <StatusBadge
          label={t(`projects.status.${project.status}`)}
          tone={project.status === 'active' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'startDate',
      header: t('projects.startDate'),
      render: (project) =>
        project.startDate ? (
          <span className="text-default-600">
            {formatDate(project.startDate, { locale })}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (project) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/projects/${project.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        breadcrumbs={[{ label: t('nav.projects') }]}
        actions={
          can(PERMISSIONS.PROJECTS_CREATE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/projects/new')}
            >
              {t('projects.create')}
            </ActionButton>
          ) : null
        }
      />

      <ViewTabs
        label={t('projects.statusLabel')}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          { value: '', label: t('common.all') },
          ...ALL_PROJECT_STATUSES.map((value) => ({
            value,
            label: t(`projects.status.${value}`),
          })),
        ]}
      />

      <Panel>
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('projects.searchPlaceholder')}
          />
        </FilterBar>

        <DataTable
          label={t('projects.title')}
          columns={columns}
          rows={projects.data?.data ?? []}
          rowKey={(project) => project.id}
          isLoading={projects.isLoading}
          error={projects.error}
          onRetry={() => void projects.refetch()}
          emptyContent={search ? t('projects.noneFound') : t('projects.noneYet')}
          page={page}
          totalPages={projects.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
